import { PDF_MIME_TYPE } from "@/pages/home/utils/convert-file-to-pdf";
import {
  getGoogleAccessToken,
  invalidateGoogleAccessToken,
} from "@/shared/utils/google/get-google-access-token";
import {
  type GooglePickerDoc,
  openGooglePicker,
} from "@/shared/utils/google/open-google-picker";
import {
  MAX_IMPORT_FILE_BYTES,
  OVERSIZED_MESSAGE,
  UNSUPPORTED_TYPE_MESSAGE,
} from "./validate-import-file";

export const DRIVE_ACCESS_MESSAGE =
  "We need access to your Google Drive to import from it.";
export const DRIVE_FETCH_MESSAGE = "Couldn't fetch the file from Google Drive.";

const GOOGLE_NATIVE_MIME_TYPES = [
  "application/vnd.google-apps.presentation",
  "application/vnd.google-apps.document",
];

// The PDF conversion keys off the file extension, and Drive names often have none.
const EXTENSION_BY_MIME: Record<string, string> = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/msword": "doc",
  "text/plain": "txt",
};

export const DRIVE_IMPORT_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "text/plain",
  ...GOOGLE_NATIVE_MIME_TYPES,
];

const DRIVE_FILES_URL = "https://www.googleapis.com/drive/v3/files";

export type PickedDriveFile = {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number | null;
  token: string;
};

export type DriveErrorStage = "drive-auth" | "drive-picker" | "drive-download";

export class DriveImportError extends Error {
  readonly code: "access" | "fetch";
  readonly stage: DriveErrorStage;
  readonly detail: string;

  constructor(
    code: "access" | "fetch",
    stage: DriveErrorStage,
    detail: string,
  ) {
    super(`${stage}: ${detail}`);
    this.name = "DriveImportError";
    this.code = code;
    this.stage = stage;
    this.detail = detail;
  }
}

const errorDetail = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

export const isGoogleNativeFile = (mimeType: string) =>
  GOOGLE_NATIVE_MIME_TYPES.includes(mimeType);

const toPickedFile = (doc: GooglePickerDoc, token: string): PickedDriveFile => {
  const size = Number(doc.sizeBytes);
  return {
    id: doc.id,
    name: doc.name ?? "Untitled",
    mimeType: doc.mimeType ?? "",
    sizeBytes: size > 0 ? size : null,
    token,
  };
};

export const pickDriveFile = async (): Promise<PickedDriveFile | null> => {
  let token: string;
  try {
    token = await getGoogleAccessToken();
  } catch (error) {
    throw new DriveImportError(
      "access",
      "drive-auth",
      errorDetail(error, "denied"),
    );
  }
  let doc: GooglePickerDoc | null;
  try {
    doc = await openGooglePicker({
      token,
      darkBackdrop: true,
      supportDrives: true,
      buildView: (picker) => {
        const docsView = (label: string) =>
          new picker.DocsView(picker.ViewId.DOCS)
            .setMimeTypes(DRIVE_IMPORT_MIME_TYPES.join(","))
            .setLabel(label);
        return [
          docsView("My Drive")
            .setParent("root")
            .setIncludeFolders(true)
            .setSelectFolderEnabled(false),
          // shared files live in their owner's Drive, never in this user's folders
          docsView("Shared with me").setOwnedByMe(false),
          docsView("Shared drives")
            .setEnableDrives(true)
            .setIncludeFolders(true)
            .setSelectFolderEnabled(false),
        ];
      },
    });
  } catch (error) {
    throw new DriveImportError(
      "fetch",
      "drive-picker",
      errorDetail(error, "load"),
    );
  }
  if (!doc) return null;
  return toPickedFile(doc, token);
};

export const validateDrivePick = (
  picked: PickedDriveFile,
): { ok: true } | { ok: false; reason: "type" | "size"; message: string } => {
  if (!DRIVE_IMPORT_MIME_TYPES.includes(picked.mimeType))
    return { ok: false, reason: "type", message: UNSUPPORTED_TYPE_MESSAGE };
  if (picked.sizeBytes !== null && picked.sizeBytes > MAX_IMPORT_FILE_BYTES)
    return { ok: false, reason: "size", message: OVERSIZED_MESSAGE };
  return { ok: true };
};

const driveFileName = (picked: PickedDriveFile) => {
  const ext = isGoogleNativeFile(picked.mimeType)
    ? "pdf"
    : EXTENSION_BY_MIME[picked.mimeType];
  const base = picked.name.trim() || "Untitled";
  if (!ext) return base;
  if (base.toLowerCase().endsWith(`.${ext}`)) return base;
  return `${base}.${ext}`;
};

const driveDownloadUrl = (picked: PickedDriveFile) => {
  const id = encodeURIComponent(picked.id);
  if (isGoogleNativeFile(picked.mimeType))
    return `${DRIVE_FILES_URL}/${id}/export?mimeType=application/pdf`;
  return `${DRIVE_FILES_URL}/${id}?alt=media&supportsAllDrives=true`;
};

export const downloadDriveFile = async (
  picked: PickedDriveFile,
  signal: AbortSignal,
): Promise<File> => {
  let res: Response;
  try {
    res = await fetch(driveDownloadUrl(picked), {
      headers: { Authorization: `Bearer ${picked.token}` },
      signal,
    });
  } catch (error) {
    if (signal.aborted) throw error;
    throw new DriveImportError("fetch", "drive-download", "network");
  }
  if (res.status === 401) {
    invalidateGoogleAccessToken();
    throw new DriveImportError("access", "drive-download", "http-401");
  }
  if (!res.ok)
    throw new DriveImportError("fetch", "drive-download", `http-${res.status}`);
  let blob: Blob;
  try {
    blob = await res.blob();
  } catch (error) {
    if (signal.aborted) throw error;
    throw new DriveImportError("fetch", "drive-download", "body");
  }
  const type = isGoogleNativeFile(picked.mimeType)
    ? PDF_MIME_TYPE
    : picked.mimeType;
  return new File([blob], driveFileName(picked), { type });
};
